import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLookup } from '../contexts/LookupContext';
import api from '../lib/api';
import { useStatusOptions } from '../lib/useStatusOptions';
import StatusListEditor from '../components/StatusListEditor';
import { useWebSocket } from '../hooks/useWebSocket';
import { useAuth } from '../contexts/AuthContext';
import { ActNotesTab } from '../components/ActNotesTab';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  CaretLeft, CaretRight, Plus, FloppyDisk, Trash, UserPlus, Phone as PhoneIcon,
  EnvelopeSimple, VideoCamera, NotePencil, Clock, Link as LinkIcon, User,
  ArrowLeft, ArrowRight, MagnifyingGlass, Notebook, CalendarBlank, FunnelSimple,
  Lock, LockOpen, Check, Circle, PencilSimple, BookmarkSimple, Sparkle,
  ChatCircleDots, ListBullets, ArrowSquareOut
} from '@phosphor-icons/react';
import LookupDialog from '../components/LookupDialog';
import ContactFilesTab from '../components/ContactFilesTab';
import ComposeEmailDialog from '../components/ComposeEmailDialog';
import { PreCallBrief } from '../components/CrunchyAgents';
import ActivityDialog from '../components/ActivityDialog';
import RelationshipDialog from '../components/RelationshipDialog';
import { ActField, ContactNameField, ActSelect, ActUserField } from '../components/ContactCardFields';
import {
  ACTIVITY_ICONS,
  ACTIVITY_COLORS_TEXT as ACTIVITY_COLORS,
  RELATIONSHIP_TYPES,
} from '../constants/contactActivity';
import { DatePicker, TimePicker, TIME_SLOTS, nextHalfHourSlot, todayISO } from '../components/DateTimePickers';

const emptyForm = {
  first_name: '', last_name: '', company: '', email: '', phone: '', title: '', department: '',
  address_street: '', address_city: '', address_state: '', address_zip: '', address_country: '',
  status: '', tags: [], notes: '', custom_fields: {}, mobile: '', fax: '', website: '', salutation: '',
  referred_by: '', last_results: '', is_private: false,
};

const TABS = [
  { id: 'activities', label: 'Activities' },
  { id: 'history', label: 'History' },
  { id: 'notes', label: 'Notes' },
  { id: 'files', label: 'Files' },
  { id: 'groups', label: 'Groups' },
  { id: 'relationships', label: 'Relationships' },
  { id: 'opportunities', label: 'Opportunities' },
  { id: 'audit', label: 'Audit Trail' },
];

export default function ContactCardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setActiveLookup, clearActiveLookup } = useLookup();
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';
  const [form, setForm] = useState({ ...emptyForm });
  const [currentId, setCurrentId] = useState(null);
  const [contactIds, setContactIds] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [totalContacts, setTotalContacts] = useState(0);
  // Pagination state so prev/next can load more contacts on demand
  // listSource: null = "all contacts" / { lookup: {conditions, logic} } = lookup result set
  const [listSource, setListSource] = useState(null);
  const [listPage, setListPage] = useState(1);
  const PAGE_SIZE = 200;
  // Lookup history (ACT! 4.0 muscle memory) — stack of past lookups, most recent first
  // Each entry: { label, source, contactIds, total }
  const [lookupHistory, setLookupHistory] = useState([]);
  const [historyIdx, setHistoryIdx] = useState(-1);
  const [saving, setSaving] = useState(false);
  const [savingLookup, setSavingLookup] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('notes');
  const [tagInput, setTagInput] = useState('');
  const [updateBanner, setUpdateBanner] = useState(null);

  // WebSocket for real-time sync
  const { connected: wsConnected, viewers, setViewing, onContactUpdated, onActivityUpdated } = useWebSocket();

  // Activity state
  const [activities, setActivities] = useState([]);
  const [composeEmailOpen, setComposeEmailOpen] = useState(false);
  const [activityDialog, setActivityDialog] = useState(false);
  const [activityForm, setActivityForm] = useState({ activity_type: 'note', title: '', content: '', duration_minutes: 0, scheduled_date: '', scheduled_time: '', priority: 'medium', recurring: false, recurrence: 'weekly', occurrences: 4, reminder_minutes: 0, outcome: '', direction: '' });

  // Relationship state
  const [relationships, setRelationships] = useState([]);
  const [relDialog, setRelDialog] = useState(false);
  const [relForm, setRelForm] = useState({ to_contact_id: '', relationship_type: 'colleague', notes: '' });
  const [relSearch, setRelSearch] = useState('');
  const [relSearchResults, setRelSearchResults] = useState([]);
  const [relSelectedContact, setRelSelectedContact] = useState(null);

  // Email tracking stats from Resend webhooks (opened/clicked counts, last events)
  const [emailStats, setEmailStats] = useState(null);

  // Audit trail
  const [auditEntries, setAuditEntries] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  // Load audit entries when Audit tab is opened
  useEffect(() => {
    if (activeTab !== 'audit' || !currentId) return;
    setAuditLoading(true);
    api.get(`/audit/contact/${currentId}`)
      .then(({ data }) => setAuditEntries(data || []))
      .catch(() => setAuditEntries([]))
      .finally(() => setAuditLoading(false));
  }, [activeTab, currentId]);

  // Lookup state
  const [lookupOpen, setLookupOpen] = useState(false);

  // Quick Lookup state (toolbar dropdown)
  const [quickLookupOpen, setQuickLookupOpen] = useState(false);
  const [quickLookupField, setQuickLookupField] = useState('last_name');
  const [quickLookupValue, setQuickLookupValue] = useState('');
  const [quickLookupSearching, setQuickLookupSearching] = useState(false);
  const quickLookupBtnRef = useRef(null);
  const [quickLookupPos, setQuickLookupPos] = useState({ top: 0, right: 0 });

  // Recompute dropdown position when it opens (and on resize/scroll while open)
  useEffect(() => {
    if (!quickLookupOpen) return;
    const updatePos = () => {
      const btn = quickLookupBtnRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      setQuickLookupPos({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    };
    updatePos();
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [quickLookupOpen]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!quickLookupOpen) return;
    const onDocClick = (e) => {
      const btn = quickLookupBtnRef.current;
      if (btn && btn.contains(e.target)) return;
      if (e.target.closest('[data-testid="quick-lookup-dropdown"]')) return;
      setQuickLookupOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [quickLookupOpen]);

  const QUICK_LOOKUP_FIELDS = [
    { value: 'first_name', label: 'First Name' },
    { value: 'last_name', label: 'Last Name' },
    { value: 'company', label: 'Company' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'address_city', label: 'City' },
    { value: 'address_state', label: 'State' },
    { value: 'address_zip', label: 'ZIP Code' },
    { value: 'status', label: 'ID/Status' },
    { value: 'tags', label: 'Tags' },
  ];

  // Push a snapshot onto the lookup history stack. Keeps the last 10.
  // Called whenever a lookup successfully changes the current contact set.
  const pushHistory = (label, source, ids, total) => {
    setLookupHistory(prev => {
      // If we're not at the top of the stack (user has gone back), truncate forward
      const base = historyIdx >= 0 ? prev.slice(prev.length - 1 - historyIdx) : prev;
      const entry = { label, source, contactIds: ids, total, ts: Date.now() };
      const next = [entry, ...base].slice(0, 10);
      return next;
    });
    setHistoryIdx(0);
  };

  const restoreFromHistory = async (idx) => {
    const entry = lookupHistory[idx];
    if (!entry) return;
    setContactIds(entry.contactIds);
    setTotalContacts(entry.total);
    setCurrentIndex(entry.contactIds.length > 0 ? 0 : -1);
    setListSource(entry.source);
    setListPage(1);
    setHistoryIdx(idx);
    if (entry.contactIds.length > 0) {
      await loadContact(entry.contactIds[0]);
    }
    setMessage(`↶ ${entry.label} (${entry.total.toLocaleString()})`);
    setTimeout(() => setMessage(''), 2500);
  };

  const runQuickLookup = async () => {
    if (!quickLookupValue.trim()) return;
    setQuickLookupSearching(true);
    try {
      const conditions = [{ field: quickLookupField, operator: '*', value: quickLookupValue }];
      const { data } = await api.post('/contacts/lookup?page=1&limit=' + PAGE_SIZE, {
        conditions, logic: 'and', mode: 'new',
      });
      if (data.contacts.length > 0) {
        const ids = data.contacts.map(c => c.contact_id);
        setContactIds(ids);
        setTotalContacts(data.total);
        setCurrentIndex(0);
        const src = { lookup: { conditions, logic: 'and' } };
        setListSource(src);
        setListPage(1);
        const fieldLabel = QUICK_LOOKUP_FIELDS.find(f => f.value === quickLookupField)?.label || quickLookupField;
        pushHistory(`${fieldLabel} starts with "${quickLookupValue}"`, src, ids, data.total);
        loadContact(data.contacts[0].contact_id);
        setMessage(`Found ${data.total} contact${data.total !== 1 ? 's' : ''}`);
      } else {
        setMessage('No contacts found');
      }
      setQuickLookupOpen(false);
      setQuickLookupValue('');
      setTimeout(() => setMessage(''), 3000);
    } catch {
      setMessage('Lookup failed');
    } finally {
      setQuickLookupSearching(false);
    }
  };

  const resetLookup = async () => {
    clearActiveLookup();
    const contacts = await loadContactIds();
    if (contacts.length > 0) {
      setCurrentIndex(0);
      await loadContact(contacts[0].contact_id);
    }
    setMessage('Showing all contacts');
    setTimeout(() => setMessage(''), 2000);
  };

  // User fields config
  const [userFieldLabels, setUserFieldLabels] = useState([]);
  const { statuses: statusOptions, save: saveStatuses } = useStatusOptions();
  const [statusEditorOpen, setStatusEditorOpen] = useState(false);
  const [dropdownLists, setDropdownLists] = useState([]);
  const [editingFieldId, setEditingFieldId] = useState(null);
  const [fieldEditorDraft, setFieldEditorDraft] = useState({ label: '', type: 'text', options: '' });
  const [savingField, setSavingField] = useState(false);

  const openFieldEditor = (field) => {
    setFieldEditorDraft({
      label: field.label,
      type: field.type || 'text',
      options: (field.options || []).join('\n'),
      list_id: field.list_id || '',
    });
    setEditingFieldId(field.id);
  };

  const saveFieldEdit = async () => {
    if (!editingFieldId) return;
    setSavingField(true);
    try {
      const useList = fieldEditorDraft.type === 'dropdown' && fieldEditorDraft.list_id;
      const opts = (fieldEditorDraft.type === 'dropdown' && !useList)
        ? fieldEditorDraft.options.split('\n').map(s => s.trim()).filter(Boolean)
        : [];
      const nextFields = userFieldLabels.map(f =>
        f.id === editingFieldId
          ? {
              ...f,
              label: fieldEditorDraft.label.trim() || f.id,
              type: fieldEditorDraft.type,
              options: opts,
              list_id: useList ? fieldEditorDraft.list_id : null,
            }
          : { id: f.id, label: f.label, type: f.type || 'text', options: f.options || [], list_id: f.list_id || null }
      );
      const { data } = await api.put('/settings/user-fields', { fields: nextFields });
      setUserFieldLabels(data.fields);
      setEditingFieldId(null);
    } catch (err) {
      console.error('Failed to save field', err);
    } finally {
      setSavingField(false);
    }
  };

  // Contact groups
  const [contactGroups, setContactGroups] = useState([]);
  const [allGroups, setAllGroups] = useState([]);

  const loadContactIds = useCallback(async () => {
    try {
      const { data } = await api.get('/contacts', { params: { page: 1, limit: PAGE_SIZE, sort_by: 'created_at', sort_order: 'desc' } });
      setContactIds(data.contacts.map(c => c.contact_id));
      setTotalContacts(data.total);
      setListSource(null);
      setListPage(1);
      return data.contacts;
    } catch { return []; }
  }, []);

  // Track the latest in-flight contact load so rapid Prev/Next clicks
  // can abort stale requests instead of queueing them up (fixes "9 requests
  // for 3 clicks → 6-second freeze" symptom).
  const loadAbortRef = useRef(null);
  // Tiny in-memory cache so Next/Prev within the lazy-loaded window feels instant.
  const contactCacheRef = useRef(new Map());

  const loadContact = useCallback(async (contactId) => {
    // Cancel previous in-flight request (if any)
    if (loadAbortRef.current) {
      try { loadAbortRef.current.abort(); } catch {}
    }
    const ctrl = new AbortController();
    loadAbortRef.current = ctrl;

    try {
      // Try cache first
      let bundle = contactCacheRef.current.get(contactId);
      if (!bundle) {
        const { data } = await api.get(`/contacts/${contactId}/full`, { signal: ctrl.signal });
        bundle = data;
        // Cache + bound size (max 50 entries — ~5 mins of browsing)
        contactCacheRef.current.set(contactId, bundle);
        if (contactCacheRef.current.size > 50) {
          const firstKey = contactCacheRef.current.keys().next().value;
          contactCacheRef.current.delete(firstKey);
        }
      }
      // Bail if the user moved on while we were loading
      if (ctrl.signal.aborted) return;

      const d = bundle.contact;
      const activities = bundle.activities || [];
      const relationships = bundle.relationships || [];

      setForm({
        first_name: d.first_name || '', last_name: d.last_name || '',
        company: d.company || '', email: d.email || '', phone: d.phone || '',
        title: d.custom_fields?.title || d.title || '', department: d.custom_fields?.department || d.department || '',
        address_street: d.address_street || '', address_city: d.address_city || '',
        address_state: d.address_state || '', address_zip: d.address_zip || '',
        address_country: d.address_country || '', status: d.status || '',
        tags: d.tags || [], notes: d.notes || '', custom_fields: d.custom_fields || {},
        mobile: d.custom_fields?.mobile || d.mobile || '', fax: d.custom_fields?.fax || d.fax || '',
        website: d.custom_fields?.website || d.website || '', salutation: d.custom_fields?.salutation || d.first_name || '',
        referred_by: d.custom_fields?.referred_by || '', last_results: d.custom_fields?.last_results || '',
        is_private: d.is_private === true,
      });
      setCurrentId(contactId);
      setActivities(activities);
      setRelationships(relationships);
      setEmailStats({
        open_count: d.email_open_count || 0,
        click_count: d.email_click_count || 0,
        bounce_count: d.email_bounce_count || 0,
        last_delivered_at: d.last_email_delivered_at || null,
        last_opened_at: d.last_email_opened_at || null,
        last_clicked_at: d.last_email_clicked_at || null,
        last_bounced_at: d.last_email_bounced_at || null,
      });
      setDirty(false);
      setMessage('');
      setUpdateBanner(null);

      // Tell WebSocket we're viewing this contact
      setViewing(contactId);
    } catch (e) {
      // Aborted requests throw — that's expected, not an error
      if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED' || ctrl.signal.aborted) return;
      setMessage('Failed to load contact');
    }
  }, []);

  useEffect(() => {
    (async () => {
      // Load user field labels
      try {
        const { data } = await api.get('/settings/user-fields');
        setUserFieldLabels(data.fields || []);
      } catch {}
      // Load reusable dropdown lists (Phase B)
      try {
        const { data } = await api.get('/dropdown-lists');
        setDropdownLists(data || []);
      } catch {}
      // Load all groups
      try {
        const { data } = await api.get('/groups');
        setAllGroups(data);
      } catch {}

      // ACT! 4.0 muscle memory: if we were navigated here from the Alarms
      // panel (or the Tasks page) with a pre-built lookup set, use it.
      // Otherwise fall through to the default "all contacts" list.
      const seeded = location.state?.lookupContactIds;
      const target = location.state?.targetContactId;
      const label = location.state?.lookupLabel;
      if (Array.isArray(seeded) && seeded.length > 0) {
        const startIdx = target ? Math.max(0, seeded.indexOf(target)) : 0;
        setContactIds(seeded);
        setTotalContacts(seeded.length);
        setCurrentIndex(startIdx);
        const src = { external: { label } };
        setListSource(src);
        setListPage(1);
        pushHistory(label || 'Lookup', src, seeded, seeded.length);
        setActiveLookup({ label: label || 'Lookup', count: seeded.length, query: null, ids: seeded });
        await loadContact(seeded[startIdx]);
        setMessage(`${label || 'Lookup'} (${seeded.length})`);
        setTimeout(() => setMessage(''), 2500);
        return;
      }

      // Fresh arrival at the Contacts card with no seeded set: this IS the
      // full database view (ACT!: clicking Contacts shows everyone), so any
      // lingering cross-page lookup is cleared.
      clearActiveLookup();
      const contacts = await loadContactIds();
      if (contacts.length > 0) {
        setCurrentIndex(0);
        await loadContact(contacts[0].contact_id);
      }
    })();
    // We deliberately only run this on mount — subsequent state changes are
    // handled by the dedicated location-state effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the user is ALREADY on /contact and clicks an alarm (or a task), the
  // page does not remount but location.state updates with a new lookup set.
  // Apply it on every fresh navigation by keying off location.key.
  // Re-clicking "Contacts" in the sidebar (new navigation, NO seeded state)
  // resets any active lookup back to the full database — ACT! muscle memory.
  const lastAppliedLookupKey = useRef(null);
  useEffect(() => {
    if (lastAppliedLookupKey.current === location.key) return;
    const isFirstRun = lastAppliedLookupKey.current === null;
    lastAppliedLookupKey.current = location.key;

    const seeded = location.state?.lookupContactIds;
    if (Array.isArray(seeded) && seeded.length > 0) {
      const target = location.state?.targetContactId;
      const label = location.state?.lookupLabel;
      const startIdx = target ? Math.max(0, seeded.indexOf(target)) : 0;
      setContactIds(seeded);
      setTotalContacts(seeded.length);
      setCurrentIndex(startIdx);
      const src = { external: { label } };
      setListSource(src);
      setListPage(1);
      pushHistory(label || 'Lookup', src, seeded, seeded.length);
      setActiveLookup({ label: label || 'Lookup', count: seeded.length, query: null, ids: seeded });
      loadContact(seeded[startIdx]);
      setMessage(`${label || 'Lookup'} (${seeded.length})`);
      setTimeout(() => setMessage(''), 2500);
      return;
    }

    if (!isFirstRun && listSource) resetLookup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Real-time update handlers
  useEffect(() => {
    onContactUpdated((msg) => {
      if (msg.contact_id === currentId) {
        setUpdateBanner(`${msg.updated_by} just updated this contact`);
        contactCacheRef.current.delete(msg.contact_id);
        // Auto-reload the contact data if we haven't made changes
        if (!dirty) {
          loadContact(msg.contact_id);
        }
        setTimeout(() => setUpdateBanner(null), 5000);
      }
    });
    onActivityUpdated((msg) => {
      if (msg.contact_id === currentId) {
        contactCacheRef.current.delete(msg.contact_id);
        // Reload activities
        api.get(`/contacts/${currentId}/activities`).then(({ data }) => setActivities(data)).catch(() => {});
      }
    });
  }, [currentId, dirty, onContactUpdated, onActivityUpdated, loadContact]);

  const updateField = (field, value) => { setForm(prev => ({ ...prev, [field]: value })); setDirty(true); };

  const newContact = () => {
    setForm({ ...emptyForm }); setCurrentId(null); setCurrentIndex(-1);
    setActivities([]); setRelationships([]); setDirty(false); setMessage('');
  };

  const handleSave = async () => {
    if (!form.first_name.trim()) { setMessage('Contact name required'); return; }
    setSaving(true); setMessage('');
    try {
      const payload = {
        first_name: form.first_name, last_name: form.last_name, company: form.company,
        email: form.email, phone: form.phone, address_street: form.address_street,
        address_city: form.address_city, address_state: form.address_state,
        address_zip: form.address_zip, address_country: form.address_country,
        status: form.status, tags: form.tags, notes: form.notes,
        title: form.title, department: form.department, mobile: form.mobile,
        fax: form.fax, website: form.website,
        custom_fields: {
          ...form.custom_fields, title: form.title, department: form.department,
          mobile: form.mobile, fax: form.fax, website: form.website,
          salutation: form.salutation, referred_by: form.referred_by, last_results: form.last_results,
        },
      };
      if (currentId) {
        await api.put(`/contacts/${currentId}`, payload);
        contactCacheRef.current.delete(currentId);
        setMessage('Saved');
      } else {
        const { data } = await api.post('/contacts', payload);
        setCurrentId(data.contact_id);
        setMessage('Created');
        const contacts = await loadContactIds();
        const idx = contacts.findIndex(c => c.contact_id === data.contact_id);
        if (idx >= 0) setCurrentIndex(idx);
      }
      setDirty(false);
      setTimeout(() => setMessage(''), 2000);
    } catch (err) { setMessage(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!currentId || !window.confirm('Delete this contact?')) return;
    try {
      await api.delete(`/contacts/${currentId}`);
      const contacts = await loadContactIds();
      if (contacts.length > 0) {
        const newIdx = Math.min(currentIndex, contacts.length - 1);
        setCurrentIndex(newIdx);
        await loadContact(contacts[newIdx].contact_id);
      } else { newContact(); }
    } catch { setMessage('Failed to delete'); }
  };

  // Save the CURRENT lookup as a Smart Group (if it was a Boolean lookup) or
  // a static List (if it came from an alarm/task lookup with explicit IDs).
  // Distinct from handleSave which only saves the open contact card.
  const saveCurrentLookup = async () => {
    if (!listSource) return;
    const isSmart = !!listSource.lookup;
    const promptLabel = isSmart
      ? "Name your Smart Group:\n\nThis saves the QUERY — every time you open it, it re-runs and includes any newly-matching contacts."
      : "Name your List:\n\nThis saves a frozen snapshot of the current contacts.";
    const defaultName = isSmart
      ? `Smart: ${(listSource.lookup.conditions || []).map(c => `${c.field} ${c.operator} ${c.value}`).join(' & ').slice(0, 50) || 'Lookup'}`
      : (listSource.external?.label || `Snapshot of ${totalContacts} contacts`);
    const name = window.prompt(promptLabel, defaultName);
    if (!name || !name.trim()) return;

    setSavingLookup(true);
    try {
      const payload = {
        name: name.trim(),
        description: '',
        group_type: isSmart ? 'smart' : 'static',
        conditions: isSmart ? listSource.lookup.conditions : [],
        logic: isSmart ? (listSource.lookup.logic || 'and') : 'and',
        current_ids: !isSmart ? contactIds : null,
      };
      const { data: created } = await api.post('/groups/from-lookup', payload);
      setMessage(
        isSmart
          ? `✨ Smart group "${created.name}" saved (${created.member_count} contacts right now)`
          : `📁 List "${created.name}" saved (${created.member_count} contacts)`
      );
      setTimeout(() => setMessage(''), 4000);
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Failed to save lookup');
      setTimeout(() => setMessage(''), 4000);
    } finally {
      setSavingLookup(false);
    }
  };

  // Auto-save helper - saves silently if there are changes.
  // `overrides` lets callers persist a value that was JUST set in state
  // (setState is async) — e.g. commitNotes passes the fresh notes string.
  const autoSave = async (overrides = null) => {
    if ((!dirty && !overrides) || !currentId || !form.first_name.trim()) return;
    try {
      const payload = {
        first_name: form.first_name, last_name: form.last_name, company: form.company,
        email: form.email, phone: form.phone, address_street: form.address_street,
        address_city: form.address_city, address_state: form.address_state,
        address_zip: form.address_zip, address_country: form.address_country,
        status: form.status, tags: form.tags, notes: form.notes,
        title: form.title, department: form.department, mobile: form.mobile,
        fax: form.fax, website: form.website,
        custom_fields: {
          ...form.custom_fields, title: form.title, department: form.department,
          mobile: form.mobile, fax: form.fax, website: form.website,
          salutation: form.salutation, referred_by: form.referred_by, last_results: form.last_results,
        },
        ...(overrides || {}),
      };
      await api.put(`/contacts/${currentId}`, payload);
      contactCacheRef.current.delete(currentId);
      setDirty(false);
    } catch {
      // Data safety: never fail silently — the user must know a save didn't land
      setMessage('⚠ Save failed — check your connection and try again');
      setTimeout(() => setMessage(''), 5000);
    }
  };

  // Keep the latest autoSave reachable from unmount cleanup + timers
  const autoSaveRef = useRef(autoSave);
  autoSaveRef.current = autoSave;

  // Debounced autosave: fires 2s after the last keystroke so typed edits
  // (notes, phone, anything) survive ANY navigation — the "lost notes" fix.
  useEffect(() => {
    if (!dirty || !currentId) return;
    const t = setTimeout(() => { autoSaveRef.current(); }, 2000);
    return () => clearTimeout(t);
  }, [form, dirty, currentId]);

  // Last-resort save when the card unmounts (sidebar nav, lookup, etc.)
  useEffect(() => () => { autoSaveRef.current(); }, []);

  // Instant commit used by the Notes tab — each note saves the moment
  // it's entered, no Save button involved.
  const commitNotes = async (newNotes) => {
    updateField('notes', newNotes);
    await autoSave({ notes: newNotes });
  };

  // Load the next page from the same source (lookup or "all contacts") and append IDs.
  // Returns the appended id list (or [] if nothing to add).
  const loadMoreContacts = useCallback(async () => {
    if (contactIds.length >= totalContacts) return [];
    const nextPage = listPage + 1;
    try {
      let appended = [];
      if (listSource?.lookup) {
        // Preserve mode + previous_conditions so pagination matches the narrowed/added set
        const { data } = await api.post(
          `/contacts/lookup?page=${nextPage}&limit=${PAGE_SIZE}`,
          { ...listSource.lookup }
        );
        appended = data.contacts.map(c => c.contact_id);
      } else {
        const { data } = await api.get('/contacts', { params: { page: nextPage, limit: PAGE_SIZE, sort_by: 'created_at', sort_order: 'desc' } });
        appended = data.contacts.map(c => c.contact_id);
      }
      if (appended.length > 0) {
        setContactIds(prev => [...prev, ...appended]);
        setListPage(nextPage);
      }
      return appended;
    } catch { return []; }
  }, [contactIds.length, totalContacts, listPage, listSource]);

  // Silent background prefetch — warms the cache for the next/prev contact
  // so the next click feels instant. Doesn't touch UI state or abort controllers.
  const prefetchContact = useCallback(async (contactId) => {
    if (!contactId || contactCacheRef.current.has(contactId)) return;
    try {
      const { data } = await api.get(`/contacts/${contactId}/full`);
      contactCacheRef.current.set(contactId, data);
      if (contactCacheRef.current.size > 50) {
        const firstKey = contactCacheRef.current.keys().next().value;
        contactCacheRef.current.delete(firstKey);
      }
    } catch {
      // Silent — prefetch failures don't matter, user-triggered load will retry
    }
  }, []);

  const goPrev = async () => {
    if (currentIndex <= 0) return;
    await autoSave();  // never lose typed edits on navigation
    const i = currentIndex - 1;
    setCurrentIndex(i);
    await loadContact(contactIds[i]);
    // Prefetch the one BEFORE this one so back-back-back is instant
    if (i > 0) prefetchContact(contactIds[i - 1]);
  };
  const goNext = async () => {
    await autoSave();  // never lose typed edits on navigation
    // Already past the last loaded contact?
    if (currentIndex >= contactIds.length - 1) {
      // But there are more in the database — fetch the next page transparently
      if (contactIds.length < totalContacts) {
        const appended = await loadMoreContacts();
        if (appended.length > 0) {
          const i = currentIndex + 1;
          setCurrentIndex(i);
          await loadContact(appended[0]);
        }
      }
      return;
    }
    const i = currentIndex + 1;
    setCurrentIndex(i);
    await loadContact(contactIds[i]);
    // Prefetch the NEXT one so next-next-next feels instant
    if (i + 1 < contactIds.length) prefetchContact(contactIds[i + 1]);
    // Eager pre-fetch: if we're near the end of the loaded batch and more exist, fetch next page in the background
    if (contactIds.length - i <= 5 && contactIds.length < totalContacts) {
      loadMoreContacts();
    }
  };

  const goToLookup = () => { navigate('/contacts'); };

  // Activity methods
  const logActivity = (type) => {
    const today = todayISO();
    const defaultTime = nextHalfHourSlot();
    const defaultTitles = {
      call: 'Phone call',
      meeting: 'Meeting',
      note: 'Note',
      email: 'Email',
      todo: 'Follow up',
      sms: 'Text message',
    };
    setActivityForm({
      activity_type: type,
      title: defaultTitles[type] || '',
      content: '',
      duration_minutes: 0,
      scheduled_date: today,
      scheduled_time: defaultTime,
      priority: 'medium',
      recurring: false,
      recurrence: 'weekly',
      occurrences: 4,
      reminder_minutes: 0,
      // Call-specific: ACT!-style outcome. Blank until user picks one.
      outcome: '',
      // SMS-specific: default direction when a user clicks the SMS button is
      // "sent" (I texted them). They can flip to "received" in the dialog.
      direction: type === 'sms' ? 'sent' : '',
    });
    setActivityDialog(true);
  };
  const addActivity = async () => {
    if (!currentId) {
      setMessage('No contact selected');
      setTimeout(() => setMessage(''), 2500);
      return;
    }
    if (!activityForm.title.trim()) {
      setMessage('Please add a title');
      setTimeout(() => setMessage(''), 2500);
      return;
    }

    // Smart intent detection: a past date on a time-based activity (call/email/meeting)
    // almost certainly means "I already did this and forgot to log it" — ask the user
    // to confirm so we mark it as completed and stamp last_X_at. Future dates flow
    // through silently as a normal schedule.
    let completed = false; // default = scheduling
    if (activityForm.activity_type !== 'note' && activityForm.scheduled_date) {
      const today = todayISO();
      if (activityForm.scheduled_date < today) {
        const ok = window.confirm(
          "That date has already passed.\n\n" +
          `Are you logging a ${activityForm.activity_type} that already happened?\n\n` +
          "• Click OK to LOG IT as completed (updates last-contact history)\n" +
          "• Click Cancel to go back and pick a future date instead"
        );
        if (!ok) return;            // user wants to fix the date
        completed = true;            // user confirmed: log as already done
      }
    }

    const payload = { ...activityForm, completed };

    try {
      if (activityForm.recurring && activityForm.scheduled_date) {
        const { data } = await api.post('/activities/recurring', {
          contact_id: currentId,
          activity_type: activityForm.activity_type,
          title: activityForm.title,
          content: activityForm.content,
          duration_minutes: activityForm.duration_minutes,
          scheduled_date: activityForm.scheduled_date,
          scheduled_time: activityForm.scheduled_time,
          priority: activityForm.priority,
          recurrence: activityForm.recurrence,
          occurrences: activityForm.occurrences,
        });
        try {
          const { data: acts } = await api.get(`/contacts/${currentId}/activities`, { params: { limit: 50 } });
          setActivities(acts);
        } catch {}
        setActivityDialog(false);
        setMessage(`Scheduled ${data.created} ${activityForm.activity_type}s`);
        setTimeout(() => setMessage(''), 2500);
      } else {
        const { data } = await api.post(`/contacts/${currentId}/activities`, payload);
        setActivities(prev => [data, ...prev]);
        contactCacheRef.current.delete(currentId); // invalidate stale cached bundle
        if (completed) {
          try { await loadContact(currentId); } catch {}
        }
        setActivityDialog(false);
        setMessage(completed ? 'Logged' : 'Saved');
        setTimeout(() => setMessage(''), 2000);
      }
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Failed to save activity');
      setTimeout(() => setMessage(''), 3000);
    }
  };
  const deleteActivity = async (id) => { try { await api.delete(`/activities/${id}`); setActivities(prev => prev.filter(a => a.activity_id !== id)); } catch {} };

  const toggleActivityComplete = async (id) => {
    try {
      const { data } = await api.put(`/activities/${id}/complete`);
      setActivities(prev => prev.map(a => a.activity_id === id ? { ...a, completed: data.completed } : a));
    } catch {}
  };

  // Relationship methods
  const searchRelContacts = async (q) => {
    setRelSearch(q);
    if (q.length < 2) { setRelSearchResults([]); return; }
    try { const { data } = await api.get('/contacts/search/quick', { params: { q } }); setRelSearchResults(data.filter(c => c.contact_id !== currentId)); } catch { setRelSearchResults([]); }
  };
  const addRelationship = async () => {
    if (!relForm.to_contact_id || !currentId) return;
    try { const { data } = await api.post(`/contacts/${currentId}/relationships`, relForm); setRelationships(prev => [...prev, data]); setRelDialog(false); setRelForm({ to_contact_id: '', relationship_type: 'colleague', notes: '' }); setRelSelectedContact(null); } catch {}
  };
  const deleteRelationship = async (id) => { try { await api.delete(`/relationships/${id}`); setRelationships(prev => prev.filter(r => r.relationship_id !== id)); } catch {} };

  const formatDate = (iso) => { if (!iso) return ''; return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); };

  const addTag = () => { const t = tagInput.trim().toLowerCase(); if (t && !form.tags.includes(t)) updateField('tags', [...form.tags, t]); setTagInput(''); };

  return (
    <div className="flex flex-col h-full" data-testid="contact-card-page">
      {/* ACT!-style Toolbar */}
      <div className="bg-gradient-to-b from-[#E8E8E8] to-[#D0D0D0] border-b border-[#999] px-2 py-1 flex items-center gap-1 shrink-0 overflow-x-auto" data-testid="act-toolbar">
        {/* Navigation */}
        <button onClick={goPrev} disabled={currentIndex <= 0} className="act-toolbar-btn" data-testid="prev-contact-btn" title="Previous">
          <ArrowLeft size={14} weight="bold" />
        </button>
        <button onClick={goNext} disabled={currentIndex >= totalContacts - 1} className="act-toolbar-btn" data-testid="next-contact-btn" title="Next">
          <ArrowRight size={14} weight="bold" />
        </button>
        <div className="w-px h-5 bg-[#999] mx-1" />

        {/* Record counter */}
        <span className="text-[11px] text-[#333] font-medium px-1 min-w-[70px] text-center" data-testid="contact-counter">
          {totalContacts === 0 ? 'New' : `${currentIndex + 1} of ${totalContacts}`}
        </span>
        <div className="w-px h-5 bg-[#999] mx-1" />

        {/* Lookup history — back/forward through past lookups (ACT! 4.0 muscle memory) */}
        <button
          onClick={() => restoreFromHistory(historyIdx + 1)}
          disabled={historyIdx >= lookupHistory.length - 1}
          className="act-toolbar-btn"
          data-testid="history-back-btn"
          title={lookupHistory[historyIdx + 1] ? `Back to: ${lookupHistory[historyIdx + 1].label}` : 'No previous lookup'}
        >
          <CaretLeft size={14} weight="bold" />
          <span className="text-[10px]">Last</span>
        </button>
        <button
          onClick={() => restoreFromHistory(historyIdx - 1)}
          disabled={historyIdx <= 0}
          className="act-toolbar-btn"
          data-testid="history-forward-btn"
          title={lookupHistory[historyIdx - 1] ? `Forward to: ${lookupHistory[historyIdx - 1].label}` : 'No next lookup'}
        >
          <span className="text-[10px]">Next</span>
          <CaretRight size={14} weight="bold" />
        </button>
        <div className="w-px h-5 bg-[#999] mx-1" />

        {/* Action buttons */}
        <button onClick={newContact} className="act-toolbar-btn" data-testid="new-contact-card-btn" title="New Contact">
          <UserPlus size={14} weight="bold" />
          <span className="text-[10px]">New</span>
        </button>
        <button onClick={() => currentId && logActivity('call')} className="act-toolbar-btn" data-testid="toolbar-call-btn" title="Log Call">
          <PhoneIcon size={14} weight="bold" />
          <span className="text-[10px]">Call</span>
        </button>
        <button onClick={() => currentId && logActivity('sms')} className="act-toolbar-btn" data-testid="toolbar-sms-btn" title="Log Text Message">
          <ChatCircleDots size={14} weight="bold" />
          <span className="text-[10px]">Text</span>
        </button>
        <button onClick={() => currentId && logActivity('meeting')} className="act-toolbar-btn" data-testid="toolbar-meeting-btn" title="Schedule Meeting">
          <CalendarBlank size={14} weight="bold" />
          <span className="text-[10px]">Meeting</span>
        </button>
        <button onClick={() => currentId && logActivity('note')} className="act-toolbar-btn" data-testid="toolbar-note-btn" title="Add Note">
          <Notebook size={14} weight="bold" />
          <span className="text-[10px]">Note</span>
        </button>
        <button onClick={() => currentId && setComposeEmailOpen(true)} className="act-toolbar-btn" data-testid="toolbar-email-btn" title="Compose Email">
          <EnvelopeSimple size={14} weight="bold" />
          <span className="text-[10px]">Email</span>
        </button>

        <div className="w-px h-5 bg-[#999] mx-1" />
        <button onClick={handleSave} disabled={saving} className="act-toolbar-btn text-[#0B5394]" data-testid="save-card-btn" title="Save changes to this contact's record">
          <FloppyDisk size={14} weight="bold" />
          <span className="text-[10px] font-semibold">{saving ? '...' : 'Save Contact'}</span>
        </button>
        {/* "Save Lookup" — only visible when there's an active lookup set.
            Saves the current view (the query OR the contact set) as a Smart
            Group / List. Kory hit this gap when looking for "Save as Smart
            Group" after running a lookup. */}
        {listSource && (listSource.lookup || listSource.external) && (
          <button
            onClick={saveCurrentLookup}
            disabled={savingLookup}
            className="act-toolbar-btn text-[#7A4F00]"
            data-testid="save-lookup-btn"
            title={
              listSource.lookup
                ? "Save this Boolean lookup as a Smart Group (auto-updates as new contacts match)"
                : "Save the current contact set as a frozen List"
            }
          >
            {listSource.lookup ? <Sparkle size={14} weight="bold" /> : <BookmarkSimple size={14} weight="bold" />}
            <span className="text-[10px] font-semibold">{savingLookup ? '...' : 'Save Lookup'}</span>
          </button>
        )}
        {currentId && (
          <button
            onClick={async () => {
              try {
                const next = !form.is_private;
                await api.put(`/contacts/${currentId}/privacy`, { is_private: next });
                setForm(prev => ({ ...prev, is_private: next }));
                setMessage(next ? 'Marked Private' : 'Marked Public');
                setTimeout(() => setMessage(''), 1500);
              } catch (err) {
                setMessage(err.response?.data?.detail || 'Failed');
                setTimeout(() => setMessage(''), 2500);
              }
            }}
            className={`act-toolbar-btn ${form.is_private ? 'text-[#C0392B]' : 'text-[#666]'}`}
            data-testid="toggle-private-btn"
            title={form.is_private ? 'Private (click to make public)' : 'Public (click to make private)'}
          >
            {form.is_private ? <Lock size={14} weight="bold" /> : <LockOpen size={14} weight="bold" />}
            <span className="text-[10px]">{form.is_private ? 'Private' : 'Public'}</span>
          </button>
        )}
        {currentId && (
          <button onClick={handleDelete} className="act-toolbar-btn text-[#C0392B]" data-testid="delete-card-btn" title="Delete">
            <Trash size={14} weight="bold" />
            <span className="text-[10px]">Delete</span>
          </button>
        )}
        <div className="flex-1" />

        {/* Live Presence - who else is viewing this contact */}
        {viewers.filter(v => v.user_id !== currentId).length > 0 && (
          <div className="flex items-center gap-1 mr-2" data-testid="presence-indicator">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-[10px] text-[#666]">
              {viewers.filter(v => v.name).map(v => v.name).join(', ')} also viewing
            </span>
          </div>
        )}

        {/* WebSocket status */}
        <div className={`w-1.5 h-1.5 rounded-full mr-1 ${wsConnected ? 'bg-green-500' : 'bg-red-400'}`} title={wsConnected ? 'Live sync connected' : 'Reconnecting...'} data-testid="ws-status" />

        {message && <span className={`text-[11px] font-medium mr-2 ${message === 'Saved' || message === 'Created' ? 'text-green-700' : message.startsWith('Found') || message === 'Showing all contacts' ? 'text-[#0B5394]' : 'text-red-700'}`} data-testid="card-message">{message}</span>}

        {/* Lookup — single button. Opens the multi-condition Boolean dialog.
            For simple one-field searches, the dialog defaults to a single
            condition so it's as fast as the old Quick Lookup dropdown. */}
        <button
          onClick={() => setLookupOpen(true)}
          className="act-toolbar-btn"
          data-testid="lookup-btn"
          title="Lookup (Boolean — use one or many conditions)"
        >
          <MagnifyingGlass size={14} weight="bold" />
          <span className="text-[10px]">Lookup</span>
        </button>
        {/* "All" — back to the full database. Only shown while a lookup or
            external contact set is active. ACT!'s Lookup → All Contacts. */}
        {listSource && (
          <button
            onClick={resetLookup}
            className="act-toolbar-btn text-[#0B5394]"
            data-testid="lookup-all-btn"
            title="Clear the current lookup and show all contacts"
          >
            <ListBullets size={14} weight="bold" />
            <span className="text-[10px] font-semibold">All</span>
          </button>
        )}
      </div>

      {/* Contact Card - 3 Column Layout */}
      <div className="flex-1 overflow-auto p-2 bg-[#E8E8E8]">
        {/* Real-time update banner */}
        {updateBanner && (
          <div className="mb-2 px-3 py-2 bg-[#DBEAFE] border border-[#93C5FD] flex items-center justify-between" data-testid="update-banner">
            <span className="text-[11px] text-[#1E40AF] font-medium">{updateBanner}</span>
            <button onClick={() => { setUpdateBanner(null); if (currentId) loadContact(currentId); }} className="text-[10px] text-[#0B5394] font-bold hover:underline">Refresh</button>
          </div>
        )}
        <div className="bg-[#F5F5F0] border border-[#999] rounded-none shadow-sm">
          <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
          {/* 3-Column Form */}
          <div className="grid grid-cols-1 lg:grid-cols-3 divide-x divide-[#CCC]">
            {/* Column 1: Business Card */}
            <div className="p-3">
              <h3 className="text-[12px] font-bold text-[#CC6600] uppercase tracking-wide mb-3" data-testid="section-business-card">Business Card</h3>
              <div className="space-y-1.5">
                <ContactNameField
                  firstName={form.first_name}
                  lastName={form.last_name}
                  onChange={(first, last) => {
                    updateField('first_name', first);
                    updateField('last_name', last);
                  }}
                  testId="card-contact-name"
                />
                <ActField label="Company" value={form.company} onChange={(v) => updateField('company', v)} testId="card-company" />
                <ActField label="Title" value={form.title} onChange={(v) => updateField('title', v)} testId="card-title" />
                <ActField label="Department" value={form.department} onChange={(v) => updateField('department', v)} testId="card-department" />
                <ActField label="Salutation" value={form.salutation} onChange={(v) => updateField('salutation', v)} testId="card-salutation" />
                <ActField label="Phone" value={form.phone} onChange={(v) => updateField('phone', v)} testId="card-phone" />
                <ActField label="Mobile" value={form.mobile} onChange={(v) => updateField('mobile', v)} testId="card-mobile" />
                <ActField label="Email" value={form.email} onChange={(v) => updateField('email', v)} testId="card-email" type="email" trailing={(form.email || '').includes('@') ? (
                  <button
                    onClick={() => setComposeEmailOpen(true)}
                    title={`Email ${form.email}`}
                    className="p-0.5 text-[#0B5394] hover:bg-[#E8F0F8] rounded"
                    data-testid="card-email-go"
                  >
                    <EnvelopeSimple size={14} weight="bold" />
                  </button>
                ) : null} />
              </div>
            </div>

            {/* Column 2: Address */}
            <div className="p-3">
              <h3 className="text-[12px] font-bold text-[#CC6600] uppercase tracking-wide mb-3" data-testid="section-address">Address</h3>
              <div className="space-y-1.5">
                <ActField label="Address 1" value={form.address_street} onChange={(v) => updateField('address_street', v)} testId="card-address1" />
                <ActField label="Address 2" value={form.custom_fields?.address2 || ''} onChange={(v) => updateField('custom_fields', { ...form.custom_fields, address2: v })} testId="card-address2" />
                <ActField label="City" value={form.address_city} onChange={(v) => updateField('address_city', v)} testId="card-city" />
                <ActField label="State" value={form.address_state} onChange={(v) => updateField('address_state', v)} testId="card-state" />
                <ActField label="ZIP" value={form.address_zip} onChange={(v) => updateField('address_zip', v)} testId="card-zip" />
                <ActField label="Country" value={form.address_country} onChange={(v) => updateField('address_country', v)} testId="card-country" />
                <ActField label="Fax" value={form.fax} onChange={(v) => updateField('fax', v)} testId="card-fax" />
                <ActField label="Web Site" value={form.website} onChange={(v) => updateField('website', v)} testId="card-website" trailing={(form.website || '').trim() ? (
                  <button
                    onClick={() => {
                      let url = form.website.trim();
                      if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
                      window.open(url, '_blank', 'noopener');
                    }}
                    title={`Open ${form.website}`}
                    className="p-0.5 text-[#0B5394] hover:bg-[#E8F0F8] rounded"
                    data-testid="card-website-go"
                  >
                    <ArrowSquareOut size={14} weight="bold" />
                  </button>
                ) : null} />
              </div>
            </div>

            {/* Column 3: Status + Latest Activities */}
            <div className="p-3">
              <h3 className="text-[12px] font-bold text-[#CC6600] uppercase tracking-wide mb-3" data-testid="section-status">Status</h3>
              <div className="space-y-1.5">
                <ActSelect label="ID/Status" value={form.status} onChange={(v) => {
                  if (v === '__edit_list__') { setStatusEditorOpen(true); return; }
                  updateField('status', v);
                }} options={[
                  ...statusOptions,
                  ...(form.status && !statusOptions.some((s) => s.value === form.status)
                    ? [{ value: form.status, label: form.status }] : []),
                  { value: '__edit_list__', label: '✎ Edit list…' },
                ]} testId="card-status" />
                <ActField label="Referred By" value={form.referred_by} onChange={(v) => updateField('referred_by', v)} testId="card-referred-by" />
              </div>
              <StatusListEditor
                open={statusEditorOpen}
                onClose={() => setStatusEditorOpen(false)}
                statuses={statusOptions}
                onSave={saveStatuses}
              />

              {/* User Fields (ACT! 4.0-style) — always visible on main card */}
              <div className="flex items-center justify-between mt-4 mb-3">
                <h3 className="text-[12px] font-bold text-[#CC6600] uppercase tracking-wide" data-testid="section-user-fields">User Fields</h3>
                <span className="text-[10px] text-[#999] italic">Click ✎ to rename</span>
              </div>
              <div className="space-y-1.5">
                {userFieldLabels.length === 0 ? (
                  <p className="text-[11px] text-[#999] italic">Customize these in Settings</p>
                ) : (() => {
                  // Anti-clutter: show only fields that are customized (renamed)
                  // or hold a value — plus ONE empty slot ready to customize.
                  // Configure the slot and the next one appears beneath it.
                  const isGeneric = (f) => /^User \d+$/i.test((f.label || '').trim());
                  const visible = [];
                  let slotAdded = false;
                  for (const f of userFieldLabels) {
                    const hasValue = !!(form.custom_fields?.[f.id]);
                    if (!isGeneric(f) || hasValue) visible.push(f);
                    else if (!slotAdded) { visible.push({ ...f, isSlot: true }); slotAdded = true; }
                  }
                  return visible.map(field => {
                    if (field.isSlot) {
                      return (
                        <button
                          key={field.id}
                          onClick={() => openFieldEditor(field)}
                          className="w-full flex items-center gap-1 text-left group"
                          title="Add a custom field"
                          data-testid="add-custom-field-slot"
                        >
                          <PencilSimple size={10} weight="bold" className="text-[#BBB] group-hover:text-[#0B5394] shrink-0" />
                          <span className="flex-1 h-[26px] px-1.5 text-[11px] italic text-[#999] group-hover:text-[#0B5394] border border-dashed border-[#C0C0C0] group-hover:border-[#0B5394] bg-white flex items-center">
                            + add custom field…
                          </span>
                        </button>
                      );
                    }
                    // Resolve list_id reference to options at render time (Phase B)
                    const resolved = field.list_id
                      ? { ...field, options: dropdownLists.find(l => l.list_id === field.list_id)?.options || [] }
                      : field;
                    return (
                      <div key={field.id} className="flex items-center gap-1">
                        {currentUser && (
                          <button
                            onClick={() => openFieldEditor(field)}
                            className="text-[#999] hover:text-[#0B5394] p-0.5 shrink-0"
                            title="Rename / change type"
                            data-testid={`edit-user-field-${field.id}`}
                          >
                            <PencilSimple size={10} weight="bold" />
                          </button>
                        )}
                        <div className="flex-1">
                          <ActUserField
                            field={resolved}
                            value={form.custom_fields?.[field.id] || ''}
                            onChange={(v) => updateField('custom_fields', { ...form.custom_fields, [field.id]: v })}
                            testId={`card-user-field-${field.id}`}
                          />
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>

              {/* Tags */}
              <div className="mt-4">
                <h3 className="text-[12px] font-bold text-[#CC6600] uppercase tracking-wide mb-2">Tags</h3>
                <div className="flex flex-wrap gap-1 mb-1.5">
                  {form.tags.map(tag => (
                    <span key={tag} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-[#0B5394] text-white text-[10px] font-medium rounded-sm">
                      {tag}
                      <button onClick={() => updateField('tags', form.tags.filter(t => t !== tag))} className="hover:text-red-300 ml-0.5">&times;</button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-1">
                  <input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    placeholder="+ tag" className="flex-1 h-[22px] px-1 text-[11px] border border-[#B0B0B0] bg-white" data-testid="card-tag-input" />
                </div>
              </div>
            </div>
          </div>

          {/* Last Results - full width */}
          <div className="px-3 pb-2 border-t border-[#CCC]">
            <div className="flex items-center gap-1 py-1.5">
              <label className="text-[11px] text-[#333] w-20 shrink-0 text-right pr-1 font-medium">Last Results</label>
              <input value={form.last_results} onChange={(e) => updateField('last_results', e.target.value)}
                autoComplete="one-time-code"
                className="flex-1 h-[26px] px-1.5 text-[12px] bg-white border border-[#B0B0B0] focus:outline-none focus:border-[#0B5394]" data-testid="card-last-results" />
            </div>
          </div>
          </form>
        </div>

        {/* Crunchy Pre-Call Brief — agent briefing before calls/meetings */}
        {currentId && <PreCallBrief contactId={currentId} />}

        {/* Bottom Tabs - ACT! style */}
        <div className="mt-1 bg-[#F5F5F0] border border-[#999]">
          <div className="flex border-b border-[#999] bg-[#E0E0E0] overflow-x-auto" data-testid="act-tabs">
            {TABS.map(tab => (
              <button
                key={tab.id}
                data-testid={`card-tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 py-1.5 text-[11px] font-medium border-r border-[#999] whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-[#F5F5F0] text-[#0B5394] border-b-2 border-b-[#0B5394]'
                    : 'text-[#555] hover:bg-[#EAEAEA]'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="min-h-[200px] max-h-[300px] overflow-y-auto">
            {/* Activities Tab */}
            {activeTab === 'activities' && (
              <div data-testid="tab-content-activities">
                {!currentId ? (
                  <p className="text-[12px] text-[#999] text-center py-8">Save contact first</p>
                ) : activities.length === 0 ? (
                  <p className="text-[12px] text-[#999] text-center py-8">No activities. Use toolbar to log calls, meetings, notes.</p>
                ) : (
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="bg-[#E0E0E0] border-b border-[#999]">
                        <th className="px-2 py-1 w-6"></th>
                        <th className="px-2 py-1 text-left font-semibold text-[#333]">Type</th>
                        <th className="px-2 py-1 text-left font-semibold text-[#333]">Date</th>
                        <th className="px-2 py-1 text-left font-semibold text-[#333]">Regarding</th>
                        <th className="px-2 py-1 text-left font-semibold text-[#333]">Details</th>
                        <th className="px-2 py-1 w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {activities.map(act => {
                        const Icon = ACTIVITY_ICONS[act.activity_type] || NotePencil;
                        const done = act.completed === true;
                        return (
                          <tr key={act.activity_id} className={`border-b border-[#DDD] hover:bg-[#E8E8E8] ${done ? 'opacity-60' : ''}`} data-testid={`activity-row-${act.activity_id}`}>
                            <td className="px-2 py-1 text-center">
                              <button
                                onClick={() => toggleActivityComplete(act.activity_id)}
                                className="p-0.5 hover:bg-green-50 rounded"
                                title={done ? 'Mark as not done' : 'Mark complete'}
                                data-testid={`complete-act-${act.activity_id}`}
                              >
                                {done
                                  ? <Check size={13} weight="bold" className="text-green-600" />
                                  : <Circle size={13} weight="bold" className="text-[#B0B0B0]" />}
                              </button>
                            </td>
                            <td className="px-2 py-1">
                              <span className={`flex items-center gap-1 capitalize ${ACTIVITY_COLORS[act.activity_type]}`}>
                                <Icon size={12} weight="bold" />{act.activity_type}
                              </span>
                            </td>
                            <td className="px-2 py-1 text-[#666]">{formatDate(act.created_at)}</td>
                            <td className={`px-2 py-1 font-medium ${done ? 'line-through text-[#999]' : 'text-[#333]'}`}>
                              {act.title}
                              {/* Call outcome pill — ACT!-style. Visible right
                                  next to the title so a scan of the timeline
                                  answers "did we actually connect?" without
                                  clicking in. */}
                              {act.activity_type === 'call' && act.outcome && (
                                <span
                                  className="ml-2 inline-block px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-wide bg-green-100 text-green-800 border border-green-300 rounded-sm align-middle"
                                  data-testid={`act-outcome-${act.activity_id}`}
                                >
                                  {act.outcome}
                                </span>
                              )}
                              {/* SMS direction arrow — → sent · ← received. */}
                              {act.activity_type === 'sms' && act.direction && (
                                <span
                                  className={`ml-2 inline-block px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-wide border rounded-sm align-middle ${
                                    act.direction === 'sent'
                                      ? 'bg-teal-100 text-teal-800 border-teal-300'
                                      : 'bg-amber-100 text-amber-800 border-amber-300'
                                  }`}
                                  data-testid={`act-direction-${act.activity_id}`}
                                >
                                  {act.direction === 'sent' ? '→ Sent' : '← Received'}
                                </span>
                              )}
                            </td>
                            <td className="px-2 py-1 text-[#666] truncate max-w-[200px]">{act.content}</td>
                            <td className="px-1 py-1">
                              <button onClick={() => deleteActivity(act.activity_id)} className="text-[#C0392B] hover:bg-red-50 p-0.5 rounded" data-testid={`del-act-${act.activity_id}`}>
                                <Trash size={11} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* History Tab */}
            {activeTab === 'history' && (
              <div className="p-3" data-testid="tab-content-history">
                {/* Email tracking summary banner — appears only when this contact has email activity */}
                {emailStats && (emailStats.open_count > 0 || emailStats.click_count > 0 || emailStats.bounce_count > 0 || emailStats.last_delivered_at) && (
                  <div className="mb-2 flex flex-wrap items-center gap-2 px-2 py-1.5 bg-[#F0F7FF] border border-[#B0CCE8] rounded text-[11px]" data-testid="email-tracking-summary">
                    <span className="font-semibold text-[#0B5394]">📧 Email tracking:</span>
                    {emailStats.last_delivered_at && (
                      <span className="text-[#444]" title={emailStats.last_delivered_at}>
                        Delivered {formatDate(emailStats.last_delivered_at)}
                      </span>
                    )}
                    {emailStats.open_count > 0 && (
                      <span className="px-1.5 py-0.5 bg-[#D4EBC8] text-[#2D5F1A] rounded-sm font-semibold" data-testid="email-open-badge" title={emailStats.last_opened_at ? `Last opened ${formatDate(emailStats.last_opened_at)}` : ''}>
                        📬 Opened {emailStats.open_count}×
                      </span>
                    )}
                    {emailStats.click_count > 0 && (
                      <span className="px-1.5 py-0.5 bg-[#FFE2A8] text-[#7A4F00] rounded-sm font-semibold" data-testid="email-click-badge" title={emailStats.last_clicked_at ? `Last clicked ${formatDate(emailStats.last_clicked_at)}` : ''}>
                        👆 Clicked {emailStats.click_count}×
                      </span>
                    )}
                    {emailStats.bounce_count > 0 && (
                      <span className="px-1.5 py-0.5 bg-[#F5C5C0] text-[#7A1A0E] rounded-sm font-semibold" data-testid="email-bounce-badge" title={emailStats.last_bounced_at ? `Last bounced ${formatDate(emailStats.last_bounced_at)}` : ''}>
                        ⚠ Bounced {emailStats.bounce_count}×
                      </span>
                    )}
                  </div>
                )}
                {activities.length === 0 ? (
                  <p className="text-[12px] text-[#999] text-center py-8">No history yet</p>
                ) : (
                  <div className="space-y-2">
                    {activities.map(act => (
                      <div key={act.activity_id} className="flex items-start gap-2 text-[11px] py-1 border-b border-[#E0E0E0]">
                        <span className="text-[#999] w-28 shrink-0">{formatDate(act.created_at)}</span>
                        <span className="capitalize text-[#0B5394] font-medium w-14">{act.activity_type}</span>
                        <span className="text-[#333] flex-1">{act.title}{act.content ? ` — ${act.content}` : ''}</span>
                        {/* Per-email status badge — populated by Resend webhook */}
                        {act.activity_type === 'email' && act.email_status && (
                          <span
                            className={
                              act.email_status === 'opened' ? 'px-1.5 py-0.5 bg-[#D4EBC8] text-[#2D5F1A] rounded-sm font-semibold shrink-0' :
                              act.email_status === 'clicked' ? 'px-1.5 py-0.5 bg-[#FFE2A8] text-[#7A4F00] rounded-sm font-semibold shrink-0' :
                              act.email_status === 'bounced' ? 'px-1.5 py-0.5 bg-[#F5C5C0] text-[#7A1A0E] rounded-sm font-semibold shrink-0' :
                              'px-1.5 py-0.5 bg-[#E0E0E0] text-[#555] rounded-sm shrink-0'
                            }
                            data-testid={`activity-email-status-${act.activity_id}`}
                          >
                            {act.email_status === 'opened' && '📬 Opened'}
                            {act.email_status === 'clicked' && '👆 Clicked'}
                            {act.email_status === 'bounced' && '⚠ Bounced'}
                            {!['opened', 'clicked', 'bounced'].includes(act.email_status) && act.email_status}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Notes Tab — ACT! 4.0 style: auto-timestamped entries, saved instantly */}
            {activeTab === 'notes' && (
              <ActNotesTab
                notes={form.notes}
                onChange={(v) => updateField('notes', v)}
                onCommit={commitNotes}
              />
            )}

            {/* Files Tab */}
            {activeTab === 'files' && currentId && (
              <ContactFilesTab contactId={currentId} />
            )}

            {/* Relationships Tab */}
            {activeTab === 'relationships' && (
              <div data-testid="tab-content-relationships">
                <div className="flex items-center justify-between px-3 py-1.5 bg-[#E0E0E0] border-b border-[#CCC]">
                  <span className="text-[11px] font-semibold text-[#333]">Linked Contacts</span>
                  {currentId && (
                    <button onClick={() => setRelDialog(true)} className="text-[11px] text-[#0B5394] hover:underline flex items-center gap-0.5" data-testid="card-add-rel-btn">
                      <Plus size={11} weight="bold" /> Add Link
                    </button>
                  )}
                </div>
                {relationships.length === 0 ? (
                  <p className="text-[12px] text-[#999] text-center py-8">No linked contacts</p>
                ) : (
                  <table className="w-full text-[11px]">
                    <thead><tr className="bg-[#E8E8E8] border-b border-[#CCC]">
                      <th className="px-2 py-1 text-left font-semibold text-[#333]">Contact</th>
                      <th className="px-2 py-1 text-left font-semibold text-[#333]">Company</th>
                      <th className="px-2 py-1 text-left font-semibold text-[#333]">Type</th>
                      <th className="px-2 py-1 w-8"></th>
                    </tr></thead>
                    <tbody>
                      {relationships.map(rel => (
                        <tr key={rel.relationship_id} className="border-b border-[#DDD] hover:bg-[#E8E8E8]" data-testid={`rel-row-${rel.relationship_id}`}>
                          <td className="px-2 py-1">
                            <button onClick={() => { setCurrentId(rel.related_contact_id); loadContact(rel.related_contact_id); }} className="text-[#0B5394] hover:underline font-medium">{rel.related_contact_name}</button>
                          </td>
                          <td className="px-2 py-1 text-[#666]">{rel.related_contact_company || '—'}</td>
                          <td className="px-2 py-1 text-[#666] capitalize">{rel.relationship_type}</td>
                          <td className="px-1 py-1"><button onClick={() => deleteRelationship(rel.relationship_id)} className="text-[#C0392B] p-0.5"><Trash size={11} /></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* Opportunities Tab */}
            {activeTab === 'opportunities' && (
              <div className="p-3 text-center" data-testid="tab-content-opportunities">
                <button onClick={() => navigate('/pipeline')} className="text-[12px] text-[#0B5394] hover:underline">Open Sales Pipeline</button>
              </div>
            )}

            {/* Groups Tab */}
            {activeTab === 'groups' && (
              <div data-testid="tab-content-groups">
                <div className="flex items-center justify-between px-3 py-1.5 bg-[#E0E0E0] border-b border-[#CCC]">
                  <span className="text-[11px] font-semibold text-[#333]">Contact Groups</span>
                </div>
                {!currentId ? (
                  <p className="text-[12px] text-[#999] text-center py-8">Save contact first</p>
                ) : (
                  <div className="p-3">
                    <p className="text-[11px] text-[#666] mb-2">Add this contact to groups:</p>
                    <div className="space-y-1">
                      {allGroups.length === 0 ? (
                        <p className="text-[12px] text-[#999]">No groups yet. <button onClick={() => navigate('/groups')} className="text-[#0B5394] hover:underline">Create a group</button></p>
                      ) : allGroups.map(g => {
                        const isMember = (form.custom_fields?.groups_list || []).includes(g.group_id) || contactGroups.includes(g.group_id);
                        return (
                          <label key={g.group_id} className="flex items-center gap-2 py-1 hover:bg-[#E8E8E8] px-2 cursor-pointer" data-testid={`group-checkbox-${g.group_id}`}>
                            <input
                              type="checkbox"
                              checked={isMember}
                              onChange={async (e) => {
                                try {
                                  if (e.target.checked) {
                                    await api.post(`/groups/${g.group_id}/members`, { contact_ids: [currentId] });
                                    setContactGroups(prev => [...prev, g.group_id]);
                                  } else {
                                    await api.delete(`/groups/${g.group_id}/members`, { data: { contact_ids: [currentId] } });
                                    setContactGroups(prev => prev.filter(id => id !== g.group_id));
                                  }
                                } catch {}
                              }}
                              className="w-3 h-3"
                            />
                            <span className="text-[12px] text-[#333]">{g.name}</span>
                            <span className="text-[10px] text-[#999]">({g.member_count})</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Audit Trail Tab */}
            {activeTab === 'audit' && (
              <div data-testid="tab-content-audit">
                {!currentId ? (
                  <p className="text-[12px] text-[#999] text-center py-8">Save contact first</p>
                ) : auditLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-4 h-4 border-2 border-[#0B5394] border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : auditEntries.length === 0 ? (
                  <p className="text-[12px] text-[#999] text-center py-8">No changes logged yet. Edit and save to see audit history here.</p>
                ) : (
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="bg-[#E0E0E0] border-b border-[#999]">
                        <th className="px-2 py-1 text-left font-semibold text-[#333]">When</th>
                        <th className="px-2 py-1 text-left font-semibold text-[#333]">Who</th>
                        <th className="px-2 py-1 text-left font-semibold text-[#333]">Field</th>
                        <th className="px-2 py-1 text-left font-semibold text-[#333]">Was</th>
                        <th className="px-2 py-1 text-left font-semibold text-[#333]">Now</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditEntries.map((a, i) => (
                        <tr key={i} className="border-b border-[#DDD] hover:bg-[#E8E8E8]" data-testid={`audit-row-${i}`}>
                          <td className="px-2 py-1 text-[#666] whitespace-nowrap">
                            {new Date(a.at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                          </td>
                          <td className="px-2 py-1 font-medium text-[#0B5394]">{a.user_name || '—'}</td>
                          <td className="px-2 py-1 text-[#333] capitalize">{(a.field || a.action || '').replace(/_/g, ' ')}</td>
                          <td className="px-2 py-1 text-[#C0392B] truncate max-w-[160px]">{a.old_value || <span className="text-[#999]">—</span>}</td>
                          <td className="px-2 py-1 text-[#1E8449] truncate max-w-[160px]">{a.new_value || <span className="text-[#999]">—</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Status Bar - ACT! style */}
      <div className="bg-[#E0E0E0] border-t border-[#999] px-3 py-1 flex items-center justify-between text-[10px] text-[#666] shrink-0" data-testid="status-bar">
        <span data-testid="status-lookup-label">
          Lookup: {listSource?.external?.label || (listSource?.lookup ? 'Custom Lookup' : 'My Contacts')}
        </span>
        <span>{totalContacts} Contacts | {activities.length} Activities</span>
      </div>

      {/* Activity Dialog */}
      <ActivityDialog
        open={activityDialog}
        onOpenChange={setActivityDialog}
        activityForm={activityForm}
        setActivityForm={setActivityForm}
        onSave={addActivity}
      />

      {/* Relationship Dialog */}
      <RelationshipDialog
        open={relDialog}
        onOpenChange={setRelDialog}
        relSearch={relSearch}
        searchRelContacts={searchRelContacts}
        relSearchResults={relSearchResults}
        setRelSearchResults={setRelSearchResults}
        setRelSearch={setRelSearch}
        relSelectedContact={relSelectedContact}
        setRelSelectedContact={setRelSelectedContact}
        relForm={relForm}
        setRelForm={setRelForm}
        onSave={addRelationship}
      />

      {/* User Field Editor Dialog (admin only) */}
      <Dialog open={!!editingFieldId} onOpenChange={(v) => !v && setEditingFieldId(null)}>
        <DialogContent className="max-w-md" data-testid="field-editor-dialog">
          <DialogHeader>
            <DialogTitle>Edit User Field — {editingFieldId}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {/* Helper: quick primer on how fields work */}
            <div className="bg-[#EEF4FB] border border-[#B8D0E8] text-[#0B5394] text-[11px] p-2 rounded-sm">
              <b>How this works:</b> type a label that makes sense to your business (e.g. <i>Lead Source</i>,
              <i> Lead Strength</i>, <i>Renewal Date</i>). Pick a type: Text for anything free-form,
              Dropdown for fixed choices, Checkbox for Yes/No, Date for dates, Number for numeric values.
            </div>
            <div>
              <label className="text-[12px] font-semibold text-[#333] block mb-1">Label (display name)</label>
              <input
                value={fieldEditorDraft.label}
                onChange={(e) => setFieldEditorDraft(d => ({ ...d, label: e.target.value }))}
                placeholder="e.g. Lead Source, Lead Strength, Renewal Date"
                autoFocus
                className="w-full h-[30px] px-2 text-[13px] border border-[#B0B0B0] bg-white focus:outline-none focus:border-[#0B5394]"
                data-testid="field-editor-label"
              />
            </div>
            <div>
              <label className="text-[12px] font-semibold text-[#333] block mb-1">Type</label>
              <select
                value={fieldEditorDraft.type}
                onChange={(e) => setFieldEditorDraft(d => ({ ...d, type: e.target.value }))}
                className="w-full h-[30px] px-2 text-[13px] border border-[#B0B0B0] bg-white focus:outline-none focus:border-[#0B5394]"
                data-testid="field-editor-type"
              >
                <option value="text">Text — free typing</option>
                <option value="dropdown">Dropdown — pick from a list</option>
                <option value="checkbox">Checkbox — Yes / No</option>
                <option value="date">Date — calendar picker</option>
                <option value="number">Number — numeric only</option>
              </select>
            </div>
            {fieldEditorDraft.type === 'dropdown' && (
              <>
                <div>
                  <label className="text-[12px] font-semibold text-[#333] block mb-1">
                    Reusable list <span className="text-[11px] text-[#999] font-normal">(optional — define lists in Settings)</span>
                  </label>
                  <select
                    value={fieldEditorDraft.list_id}
                    onChange={(e) => setFieldEditorDraft(d => ({ ...d, list_id: e.target.value }))}
                    className="w-full h-[30px] px-2 text-[13px] border border-[#B0B0B0] bg-white focus:outline-none focus:border-[#0B5394]"
                    data-testid="field-editor-list-id"
                  >
                    <option value="">— Use inline options below —</option>
                    {dropdownLists.map(l => (
                      <option key={l.list_id} value={l.list_id}>{l.name} ({l.options.length} options)</option>
                    ))}
                  </select>
                </div>
                {!fieldEditorDraft.list_id && (
                  <div>
                    <label className="text-[12px] font-semibold text-[#333] block mb-1">
                      Inline options <span className="text-[11px] text-[#999] font-normal">(one per line)</span>
                    </label>
                    <textarea
                      value={fieldEditorDraft.options}
                      onChange={(e) => setFieldEditorDraft(d => ({ ...d, options: e.target.value }))}
                      rows={6}
                      placeholder={"Hot\nMild\nCool\nCold"}
                      className="w-full px-2 py-1 text-[13px] font-mono border border-[#B0B0B0] bg-white focus:outline-none focus:border-[#0B5394] leading-tight"
                      data-testid="field-editor-options"
                    />
                  </div>
                )}
              </>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-[#E0E0E0]">
            <Button
              variant="outline"
              onClick={() => setEditingFieldId(null)}
              data-testid="field-editor-cancel"
            >Cancel</Button>
            <Button
              onClick={saveFieldEdit}
              disabled={savingField}
              className="bg-[#0B5394] hover:bg-[#073763] text-white"
              data-testid="field-editor-save"
            >{savingField ? 'Saving...' : 'Save'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lookup Dialog */}
      <LookupDialog
        open={lookupOpen}
        onOpenChange={setLookupOpen}
        currentIds={contactIds}
        currentTotal={totalContacts}
        currentQuery={listSource?.lookup || null}
        onResults={(data) => {
          if (data.contacts.length > 0) {
            const ids = data.contacts.map(c => c.contact_id);
            setContactIds(ids);
            setTotalContacts(data.total);
            setCurrentIndex(0);
            if (data._query) {
              const src = { lookup: data._query };
              setListSource(src);
              setListPage(1);
              const conds = data._query.conditions || [];
              const label = conds.map(c => `${c.field} ${c.operator} ${c.value}`).join(', ').slice(0, 60);
              pushHistory(label || 'Advanced lookup', src, ids, data.total);
              // Cross-page filter: Contact List (and the status bar) now
              // honor this lookup until it's cleared. ACT! 4.0 behavior.
              setActiveLookup({ label: label || 'Lookup', count: data.total, query: data._query, ids: null });
            }
            loadContact(data.contacts[0].contact_id);
            // NOTE: we intentionally KEEP the Lookup dialog open here so the
            // user can still see the result count and click "Save as Smart
            // Group" / "Save as Group". Previously this auto-closed the
            // dialog and the Save buttons vanished — Kory hit this bug.
          }
        }}
      />

      {/* Compose Email Dialog — auto-logs as Email activity on send */}
      <ComposeEmailDialog
        open={composeEmailOpen}
        contact={{ ...form, contact_id: currentId }}
        onClose={() => setComposeEmailOpen(false)}
        onSent={() => {
          if (currentId) {
            contactCacheRef.current.delete(currentId);
            api.get(`/contacts/${currentId}/activities`)
              .then(({ data }) => setActivities(data))
              .catch(() => {});
          }
          setMessage('Email sent ✓');
          setTimeout(() => setMessage(''), 3000);
        }}
      />
    </div>
  );
}
